import { CalculatorInputs, CalculatorResults, Currency } from "./types";

// Conversion rates from SEK (base currency)
const CONVERSION_RATES: Record<Currency, number> = {
  SEK: 1,
  EUR: 0.088,  // 1 SEK ≈ 0.088 EUR
  USD: 0.095,  // 1 SEK ≈ 0.095 USD
};

export const calculateHotelCost = (
  employees: number,
  nights: number,
  hotelRate: number,
  extraFees: number
): number => {
  return employees * nights * hotelRate + employees * nights * extraFees;
};

export const calculateGuestlyCost = (
  employees: number,
  nights: number,
  guestlyRate: number
): number => {
  return employees * nights * guestlyRate;
};

export const calculateDirectSavings = (
  hotelCost: number,
  guestlyCost: number
): number => {
  return hotelCost - guestlyCost;
};

export const calculateGymValue = (
  employees: number,
  nights: number,
  includeGym: boolean
): number => {
  if (!includeGym) return 0;
  const months = Math.ceil(nights / 30);
  return employees * months * 700;
};

export const calculateTotalValue = (
  directSavings: number,
  gymValue: number
): number => {
  return directSavings + gymValue;
};

export const calculateAllResults = (
  inputs: CalculatorInputs
): CalculatorResults => {
  const hotelCost = calculateHotelCost(
    inputs.employees,
    inputs.nights,
    inputs.hotelRate,
    inputs.extraFees
  );
  const guestlyCost = calculateGuestlyCost(
    inputs.employees,
    inputs.nights,
    inputs.guestlyRate
  );
  const directSavings = calculateDirectSavings(hotelCost, guestlyCost);
  const gymValue = calculateGymValue(
    inputs.employees,
    inputs.nights,
    inputs.includeGymValue
  );
  const totalAdvantage = calculateTotalValue(directSavings, gymValue);

  return {
    hotelCost,
    guestlyCost,
    directSavings,
    gymValue,
    totalAdvantage,
  };
};

export const formatCurrency = (amount: number, currency: Currency = "SEK"): string => {
  const convertedAmount = amount * CONVERSION_RATES[currency];
  
  const localeMap: Record<Currency, string> = {
    SEK: "sv-SE",
    EUR: "de-DE",
    USD: "en-US",
  };

  const currencySymbols: Record<Currency, string> = {
    SEK: "kr",
    EUR: "€",
    USD: "$",
  };

  const formatted = new Intl.NumberFormat(localeMap[currency], {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(convertedAmount);

  return currency === "SEK" ? `${formatted} kr` : 
         currency === "EUR" ? `€${formatted}` : 
         `$${formatted}`;
};
